<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 ms-3 overflow-visible me-3 pt-3">
        <div class="stripe-configuration">
          <h2>{{ editMode ? 'Edit' : 'Import' }} Calendar</h2>

          <form @submit.prevent="saveDetails">
            <div class="form-group">
              <label for="name">League name: <span class="required-mark">*</span></label>
              <input id="name" v-model="editedConfig.name" type="text" class="form-control" :class="{ 'is-invalid': visibleErrors.name }" :maxlength="limits.name" style="min-width: 500px;">
              <div v-if="visibleErrors.name" class="invalid-feedback d-block">{{ visibleErrors.name }}</div>
            </div>
            <div class="form-group">
              <label for="shortName">Short name: <span class="required-mark">*</span></label>
              <input id="shortName" v-model="editedConfig.shortName" type="text" class="form-control" :class="{ 'is-invalid': visibleErrors.shortName }" :maxlength="limits.shortName" style="min-width: 200px;">
              <div v-if="visibleErrors.shortName" class="invalid-feedback d-block">{{ visibleErrors.shortName }}</div>
            </div>
            <div class="form-group">
              <label for="sport">Sport: <span class="required-mark">*</span></label>
              <select id="sport" v-model="editedConfig.sport" class="form-select" :class="{ 'is-invalid': visibleErrors.sport }" style="min-width: 200px;">
                <option v-for="s in sportOptions" :key="s" :value="s">{{ s }}</option>
              </select>
              <div v-if="visibleErrors.sport" class="invalid-feedback d-block">{{ visibleErrors.sport }}</div>
            </div>
            <div class="form-group">
              <label for="calendarSourceUrl">Source URL:</label>
              <input id="calendarSourceUrl" v-model="editedConfig.calendarSourceUrl" type="url" class="form-control" placeholder="Where you got this calendar's .ics file from" style="min-width: 500px;">
            </div>
            <p class="small text-muted"><span class="required-mark">*</span> Required fields</p>

            <div v-if="editMode" class="form-group">
              <label for="uid">Calendar UID:</label>
              <input id="uid" v-model="editedConfig.uid" type="text" class="form-control" style="min-width: 500px;" disabled>
            </div>

            <div v-if="editMode" class="form-group">
              <label for="status">
                <input id="status" type="checkbox" class="checkbox" v-model="editedConfig.status">
                Status
              </label>
            </div>
            <div v-if="editMode" class="form-group">
              <label for="active">
                <input id="active" type="checkbox" class="checkbox" v-model="editedConfig.active">
                Active
              </label>
            </div>

            <div v-if="editMode">
              <div v-if="error !== ''" class="error-message">{{ error }}</div>
              <br>
              <a role="button" class="procricket-btn" :class="{ disabled: saving }" @click="saveDetails">Save Changes</a>
              <a role="button" class="ms-3 procricket-btn--reverse" @click="cancelEdit"> Cancel </a>
            </div>
            <!-- New league without a file: saves just the league (no matches). The .ics can be
                 uploaded later from its Edit page ("Re-upload calendar"), which also sets its uid. -->
            <div v-else>
              <div v-if="error !== ''" class="error-message">{{ error }}</div>
              <br>
              <a role="button" class="procricket-btn" :class="{ disabled: saving || !!previewResult }" @click="saveWithoutFile">Save league without file</a>
              <a role="button" class="ms-3 procricket-btn--reverse" @click="cancelEdit"> Cancel </a>
              <p class="small text-muted mt-2 mb-0">
                Or upload the league's .ics below to import its matches together with it.
              </p>
            </div>
          </form>

          <!-- STEP 1: PARSE / RE-UPLOAD -->
          <div class="box-outline mt-4 p-3">
            <h5>{{ editMode ? 'Re-upload calendar' : '.ics file' }}</h5>
            <p v-if="editMode" class="small text-muted">
              Upload a newer version of this calendar's file. Nothing changes until you review the parsed
              matches below and confirm — existing matches are matched on their own UID.
            </p>
            <p v-else class="small text-muted">
              Parsing only previews the matches — nothing is written to the database until you review them
              below and click Confirm.
            </p>
            <input ref="icsFile" type="file" accept=".ics" class="form-control mb-2" style="min-width: 500px;" @change="onFileSelected">
            <a role="button" class="procricket-btn" :class="{ disabled: !icsFile || parsing }" @click="doPreview">
              {{ parsing ? 'Parsing...' : 'Parse & Preview' }}
            </a>
            <div v-if="parseError !== ''" class="error-message mt-2">{{ parseError }}</div>
          </div>

          <!-- STEP 2: REVIEW / EDIT / CONFIRM -->
          <div v-if="previewResult" class="mt-4">
            <div class="import-summary-banner p-3 mb-3">
              <strong>{{ lastFileName }}</strong>
              <span v-if="previewResult.calendarName">("{{ previewResult.calendarName }}")</span> —
              {{ previewResult.matchesParsed }} matches parsed,
              {{ previewResult.teamsMatched }} teams matched,
              {{ previewResult.errors.length }} error{{ previewResult.errors.length === 1 ? '' : 's' }}
              <ul v-if="previewResult.errors.length" class="mb-0 mt-2">
                <li v-for="(err, i) in previewResult.errors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <h4>Review matches before inserting</h4>
            <p class="small text-muted">
              Uncheck a row to leave it out entirely, or edit any field that came through wrong. Nothing is
              saved until you click Confirm.
            </p>

            <div class="box-outline p-3 mb-3">
              <h6>Apply to all matches</h6>
              <p class="small text-muted mb-2">
                Per-feed stream start/end timing is no longer set here — configure it per source
                on the Matches screen after confirming, where it's set relative to each match's
                own start time.
              </p>
              <div class="d-inline-flex align-items-end flex-wrap" style="gap: 1rem;">
                <div class="form-group mb-0">
                  <label for="bulkFormat">Format:</label>
                  <select id="bulkFormat" class="form-select form-select-sm" v-model="bulkFormat">
                    <option :value="undefined">—</option>
                    <option v-for="f in formatOptions" :key="f" :value="f">{{ f }}</option>
                  </select>
                </div>
                <a role="button" class="procricket-btn--reverse" @click="applyBulkToAll">Apply to all</a>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-striped table-bordered">
                <thead>
                <tr>
                  <th class="text-center">Include</th>
                  <th>Day</th>
                  <th>Team 1</th>
                  <th>Team 2</th>
                  <th>Venue</th>
                  <th style="width: 200px;">Start ({{ tz }}) <span class="required-mark">*</span></th>
                  <th style="width: 200px;">End ({{ tz }})</th>
                  <th>Format</th>
                  <th>Cal Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in previewRows" :key="row.key" :class="{ 'row-excluded': !row.included }">
                  <td class="text-center">
                    <input type="checkbox" v-model="row.included">
                  </td>
                  <td>{{ dayLabel(row) }}</td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.team1" :disabled="!row.included"></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.team2" :disabled="!row.included"></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.venue" :disabled="!row.included"></td>
                  <td>
                    <input type="datetime-local" class="form-control form-control-sm" style="width: 180px;"
                           :class="{ 'is-invalid': rowError(row, 'startTime') }"
                           :title="rowError(row, 'startTime')"
                           :value="toDateTimeLocal(row.startTime)"
                           @change="row.startTime = fromDateTimeLocal($event.target.value)"
                           :disabled="!row.included">
                  </td>
                  <td>
                    <input type="datetime-local" class="form-control form-control-sm" style="width: 180px;"
                           :class="{ 'is-invalid': rowError(row, 'endTime') }"
                           :title="rowError(row, 'endTime')"
                           :value="toDateTimeLocal(row.endTime)"
                           @change="row.endTime = fromDateTimeLocal($event.target.value)"
                           :disabled="!row.included">
                  </td>
                  <td>
                    <select class="form-select form-select-sm" v-model="row.format" :disabled="!row.included">
                      <option :value="undefined">—</option>
                      <option v-for="f in formatOptions" :key="f" :value="f">{{ f }}</option>
                    </select>
                  </td>
                  <td>
                    <select class="form-select form-select-sm" v-model="row.calStatus" :disabled="!row.included">
                      <option v-for="s in calStatusOptions" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div v-if="confirmError !== ''" class="error-message mt-2">{{ confirmError }}</div>

            <a role="button" class="procricket-btn" :class="{ disabled: confirming || includedCount === 0 }" @click="doConfirm">
              {{ confirming ? 'Inserting...' : `Confirm & Insert ${includedCount} match${includedCount === 1 ? '' : 'es'}` }}
            </a>
            <a role="button" class="ms-3 procricket-btn--reverse" @click="discardPreview">Discard preview</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Header from '@/components/Header.vue'
import { CalLeague, CalMatchDraft } from '@/model/models'
import AdminService from "@/services/admin-service";
import { toDateTimeLocal as isoToLocalInput, fromDateTimeLocal as localInputToIso, localTimezoneLabel } from "@/utils/general-functions";

export default defineComponent({
  name: 'CalLeagueEdit',
  components: {
    Header,
  },
  data() {
    return {
      tz: localTimezoneLabel(),
      editedConfig: {
        calLeagueId: this.$route.params.calLeagueId,
        name: this.$route.params.name,
        shortName: this.$route.params.shortName,
        // Suggested as CRICKET on a first import (no :sport route param yet), same default
        // the BE itself falls back to - the admin can change it before confirming/saving.
        sport: this.$route.params.sport || 'CRICKET',
        uid: this.$route.params.uid,
        calendarSourceUrl: this.$route.params.calendarSourceUrl,
        lastImport: this.$route.params.lastImport,
        status: this.$route.params.status === 'true' || this.$route.params.status === true,
        active: this.$route.params.active === 'true' || this.$route.params.active === true
      },
      editMode: false,
      icsFile: null,
      lastFileName: '',
      parsing: false,
      parseError: "",
      previewResult: null,
      previewRows: [],
      confirming: false,
      confirmError: "",
      saving: false,
      error: "",
      // Empty until created()'s AdminService.getCalendarStaticData() call resolves and
      // fills these in from the BE - the BE's enum lists are the source of truth for what's
      // selectable, not a locally hardcoded copy.
      formatOptions: [],
      calStatusOptions: [],
      sportOptions: [],
      bulkFormat: undefined,
      // Off until the first save/confirm attempt, so a fresh form isn't covered in red - from then on
      // the messages update live as the admin fixes each field.
      showValidation: false,
      // Column lengths of cal_league (CalLeagueEntity).
      limits: { name: 100, shortName: 20 }
    }
  },
  computed: {
    includedCount() {
      return this.previewRows.filter(r => r.included).length;
    },
    // Mirrors what the BE needs: name, shortName and sport are non-null on both CalLeague and
    // CalLeagueConfirmRequest - a missing one fails deserialization with a raw JSON parse error.
    leagueErrors() {
      const errors = {};
      const name = (this.editedConfig.name || '').trim();
      const shortName = (this.editedConfig.shortName || '').trim();
      if (!name) {
        errors.name = 'League name is required.';
      } else if (name.length > this.limits.name) {
        errors.name = `League name can be at most ${this.limits.name} characters.`;
      }
      if (!shortName) {
        errors.shortName = 'Short name is required.';
      } else if (shortName.length > this.limits.shortName) {
        errors.shortName = `Short name can be at most ${this.limits.shortName} characters.`;
      }
      if (!this.editedConfig.sport) {
        errors.sport = 'Sport is required.';
      }
      return errors;
    },
    // Only included rows are sent - CalMatchDraft.startTime is non-null on the BE.
    rowErrors() {
      const errors = {};
      this.previewRows.filter(r => r.included).forEach((row) => {
        if (!row.startTime) {
          errors[row.key] = { startTime: 'Start time is required.' };
        } else if (row.endTime && new Date(row.endTime) <= new Date(row.startTime)) {
          errors[row.key] = { endTime: 'End time must be after the start time.' };
        }
      });
      return errors;
    },
    visibleErrors() {
      return this.showValidation ? this.leagueErrors : {};
    }
  },
  created() {
    if (this.$route.params.calLeagueId) {
      this.editMode = true;
    }
    AdminService.getCalendarStaticData().then((data) => {
      this.formatOptions = data.matchFormats;
      this.calStatusOptions = data.calStatuses;
      this.sportOptions = data.sports;
    }).catch((error) => {
      console.error("Error loading calendar static data:", error);
    });
  },
  methods: {
    onFileSelected(event) {
      this.icsFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    },
    dayLabel(row) {
      if (!row.parentUid) return '—';
      const siblings = this.previewRows.filter(r => r.parentUid === row.parentUid);
      if (siblings.length < 2) return '—';
      const idx = siblings.indexOf(row);
      return `Day ${idx + 1} of ${siblings.length}`;
    },
    toDateTimeLocal(iso) {
      return isoToLocalInput(iso);
    },
    fromDateTimeLocal(value) {
      return localInputToIso(value);
    },
    applyBulkToAll() {
      this.previewRows.forEach((row) => {
        row.format = this.bulkFormat;
      });
    },
    doPreview() {
      if (!this.icsFile || this.parsing) return;
      this.parseError = "";
      // A rejected file (e.g. a different calendar's uid) must not leave the previous file's
      // preview sitting on screen looking like the result of this upload.
      this.previewResult = null;
      this.previewRows = [];
      this.parsing = true;
      this.lastFileName = this.icsFile.name;

      const calLeagueId = this.editMode ? Number(this.editedConfig.calLeagueId) : undefined;
      AdminService.previewCalLeague(this.icsFile, calLeagueId)
          .then((result) => {
            this.previewResult = result;
            this.previewRows = result.matches.map((m, i) => ({
              key: m.uid || `row-${i}`,
              // Already-passed matches aren't worth importing by default - re-uploading an
              // .ics tends to include everything from the season, not just what's new. Still
              // shown (and still checkable) rather than dropped, in case an admin genuinely
              // wants to backfill one.
              included: new Date(m.startTime) >= new Date(),
              uid: m.uid,
              parentUid: m.parentUid,
              team1: m.team1,
              team2: m.team2,
              venue: m.venue,
              startTime: m.startTime,
              endTime: m.endTime,
              format: m.format,
              calStatus: m.calStatus
            }));
            // The file names its own series page; offer it as the league's source URL unless the admin already set one.
            if (result.scheduleUrl && !this.editedConfig.calendarSourceUrl) {
              this.editedConfig.calendarSourceUrl = result.scheduleUrl;
            }
          }).catch((error) => {
            this.parseError = this.extractErrorMessage(error);
          }).finally(() => {
            this.parsing = false;
          });
    },
    discardPreview() {
      this.previewResult = null;
      this.previewRows = [];
      this.icsFile = null;
      if (this.$refs.icsFile) this.$refs.icsFile.value = '';
    },
    doConfirm() {
      if (this.confirming || this.includedCount === 0) return;
      this.confirmError = this.validate(true);
      if (this.confirmError) return;
      this.confirming = true;

      const matches = this.previewRows.filter(r => r.included).map(r => new CalMatchDraft(
          r.uid, r.parentUid, r.team1, r.team2, r.venue, r.startTime, r.endTime, r.format, r.calStatus
      ));
      const calLeagueId = this.editMode ? Number(this.editedConfig.calLeagueId) : undefined;
      const calendarUid = this.previewResult ? this.previewResult.calendarUid : undefined;

      AdminService.confirmCalLeagueImport(this.editedConfig.name.trim(), this.editedConfig.shortName.trim(), matches, calLeagueId, calendarUid, this.editedConfig.sport, this.editedConfig.calendarSourceUrl)
          .then(() => {
            alert('Import complete');
            this.$router.push({ name: 'CalLeagues' });
          }).catch((error) => {
            this.confirmError = this.extractErrorMessage(error);
          }).finally(() => {
            this.confirming = false;
          });
    },
    // Creates the league with no matches - confirm with an empty list. Its .ics (and with it the
    // calendar uid) can be uploaded later from the Edit page.
    saveWithoutFile() {
      if (this.saving || this.editMode || this.previewResult) return;
      this.error = this.validate(false);
      if (this.error) return;
      this.saving = true;
      AdminService.confirmCalLeagueImport(this.editedConfig.name.trim(), this.editedConfig.shortName.trim(), [], undefined, undefined, this.editedConfig.sport, this.editedConfig.calendarSourceUrl)
          .then(() => {
            alert('League saved');
            this.$router.push({ name: 'CalLeagues' });
          }).catch((error) => {
            this.error = this.extractErrorMessage(error);
          }).finally(() => {
            this.saving = false;
          });
    },
    saveDetails() {
      if (this.saving || !this.editMode) return;
      this.error = this.validate(false);
      if (this.error) return;

      const dataToSave = new CalLeague(
          Number(this.editedConfig.calLeagueId),
          this.editedConfig.name.trim(),
          this.editedConfig.shortName.trim(),
          this.editedConfig.sport,
          this.editedConfig.uid,
          this.editedConfig.calendarSourceUrl,
          this.editedConfig.lastImport,
          this.editedConfig.status,
          this.editedConfig.active
      );
      this.saving = true;
      AdminService.updateCalLeague(dataToSave).then(() => {
        alert('Update complete');
        this.$router.push({ name: 'CalLeagues' });
      }).catch((error) => {
        this.error = this.extractErrorMessage(error);
      }).finally(() => {
        this.saving = false;
      });
    },
    // Turns the per-field messages on and returns a summary for the message next to the clicked
    // button, or "" when the request can be sent.
    validate(includeMatches) {
      this.showValidation = true;
      const labels = { name: 'League name', shortName: 'Short name', sport: 'Sport' };
      const invalid = Object.keys(this.leagueErrors).map(k => labels[k]);
      if (includeMatches) {
        const badRows = Object.keys(this.rowErrors).length;
        if (badRows) {
          invalid.push(`start/end time of ${badRows} match${badRows === 1 ? '' : 'es'}`);
        }
      }
      return invalid.length ? `Please fix the highlighted fields: ${invalid.join(', ')}.` : "";
    },
    rowError(row, field) {
      if (!this.showValidation || !this.rowErrors[row.key]) return '';
      return this.rowErrors[row.key][field] || '';
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    },
    cancelEdit() {
      this.$router.back();
    }
  }
})
</script>
<style src="../../assets/style.css">
</style>
